import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { StepComponentProps } from "../../components/MultiStepGradeModal";
import { ExitTicketGeneratorCreateRequestDto } from "@packages/api/madlenAPI.schemas";

export const ExitTicketAdvancedStep: React.FC<
  StepComponentProps<ExitTicketGeneratorCreateRequestDto>
> = ({ data, onDataChange, themeColor }) => {
  const { t } = useTranslation();
  const [showCustomPurpose, setShowCustomPurpose] = useState(false);

  const complexityOptions = [
    {
      id: "QUICK",
      emoji: "⚡",
      label: t("exitTickets.complexity.quick", "Hızlı"),
      desc: t("exitTickets.complexity.quickDesc", "Basit ve kısa sorular"),
    },
    {
      id: "DETAILED",
      emoji: "🔍",
      label: t("exitTickets.complexity.detailed", "Detaylı"),
      desc: t(
        "exitTickets.complexity.detailedDesc",
        "Kapsamlı ve derinlemesine sorular",
      ),
    },
    {
      id: "MIXED",
      emoji: "🎯",
      label: t("exitTickets.complexity.mixed", "Karışık"),
      desc: t(
        "exitTickets.complexity.mixedDesc",
        "Basit ve karmaşık soruların karışımı",
      ),
    },
  ];

  const purposeOptions = [
    {
      id: "INSTRUCTIONAL_FEEDBACK",
      emoji: "📚",
      label: t(
        "exitTickets.purpose.instructionalFeedback",
        "Öğretimsel Geri Bildirim",
      ),
      desc: t(
        "exitTickets.purpose.instructionalFeedbackDesc",
        "Öğrenme sürecini değerlendirmek için",
      ),
    },
    {
      id: "AFFECTIVE_REFLECTION",
      emoji: "💭",
      label: t("exitTickets.purpose.affectiveReflection", "Duygusal Yansıma"),
      desc: t(
        "exitTickets.purpose.affectiveReflectionDesc",
        "Öğrencilerin duygusal deneyimlerini anlamak için",
      ),
    },
  ];

  const handleComplexitySelect = (complexity: string) => {
    onDataChange({ complexity });
  };

  const handlePurposeSelect = (purpose: string) => {
    onDataChange({ purpose });
    setShowCustomPurpose(false);
  };

  const handleOthersClick = () => {
    onDataChange({ purpose: "" });
    setShowCustomPurpose(true);
  };

  // Helper function to check if "Others" option should be selected
  const isOthersSelected = () => {
    return (
      showCustomPurpose ||
      (!purposeOptions.some((option) => option.id === data.purpose) &&
        !!data.purpose)
    );
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-teal-500 dark:from-green-300 dark:to-teal-400">
          {t("exitTickets.steps.options.title", "Seçenekler ve Ayarlar")}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t(
            "exitTickets.steps.options.description",
            "Çıkış biletinizin karmaşıklığını ve amacını seçin",
          )}
        </p>
      </div>

      {/* Complexity Selection */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <label className="block text-sm font-medium text-green-700 dark:text-green-300">
            {t("exitTickets.complexity.title", "Karmaşıklık Seviyesi")}
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {complexityOptions.map((option) => (
            <div
              key={option.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                data.complexity === option.id
                  ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handleComplexitySelect(option.id)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    data.complexity === option.id
                      ? "border-green-500 bg-green-500"
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {data.complexity === option.id && (
                    <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{option.emoji}</span>
                    <h3 className="font-medium text-base">{option.label}</h3>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {option.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Purpose Selection */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <label className="block text-sm font-medium text-green-700 dark:text-green-300">
            {t("exitTickets.purpose.title", "Çıkış Biletinin Amacı")}
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {purposeOptions.map((option) => (
            <div
              key={option.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                data.purpose === option.id
                  ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handlePurposeSelect(option.id)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    data.purpose === option.id
                      ? "border-green-500 bg-green-500"
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {data.purpose === option.id && (
                    <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{option.emoji}</span>
                    <h3 className="font-medium text-base">{option.label}</h3>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {option.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {/* Others Option */}
          <div
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
              isOthersSelected()
                ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
            }`}
            onClick={handleOthersClick}
          >
            <div className="flex items-start gap-3">
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                  isOthersSelected()
                    ? "border-green-500 bg-green-500"
                    : "border-gray-300 dark:border-gray-600"
                }`}
              >
                {isOthersSelected() && (
                  <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-lg">✏️</span>
                  <h3 className="font-medium text-base">
                    {t("exitTickets.purpose.others", "Diğer")}
                  </h3>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {t(
                    "exitTickets.purpose.othersDesc",
                    "Özel amaçınızı belirtin",
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Purpose Textarea */}
      {showCustomPurpose && (
        <div className="space-y-3">
          <label className="block text-sm font-medium text-green-700 dark:text-green-300">
            {t(
              "exitTickets.purpose.customPlaceholder",
              "Çıkış biletinizin amacını açıklayın...",
            )}
          </label>
          <textarea
            value={data.purpose || ""}
            onChange={(e) => onDataChange({ purpose: e.target.value })}
            className="w-full rounded-xl border-2 border-gray-200 dark:border-gray-700 p-4 text-base focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200"
            placeholder={t(
              "exitTickets.purpose.customPlaceholder",
              "Çıkış biletinizin amacını açıklayın...",
            )}
            rows={4}
          />
        </div>
      )}
    </div>
  );
};
